/* eslint-disable max-len */
import React, { JSX, useState } from 'react';
import {
    Button, FormControl, FormGroup, FormLabel, Row, Col, Form,
} from "react-bootstrap";
import {useSelector, useDispatch} from 'react-redux';
import { BsExclamationTriangleFill } from "react-icons/bs";
import {useAppInsightsContext} from "@microsoft/applicationinsights-react-js";
import getBrandName from "app/shared/utils/BrandHelper";
import { isEmailvalid} from "app/shared/utils/helperFunctions";
import logging from "app/shared/utils/logging";
import { useForgotPasswordMutation } from "app/services/api/mutateGateAPI";
import {setForgotPasswordMsg, setRedirectToMyAccount} from "app/store/slices/authSlice";
import {decrypt} from "app/shared/utils/EncryptionUtility";
import NotificationBanner from "app/shared/components/NotificationBanner";
import gRecaptchaManager from 'app/features/GRecaptcha/GRecaptchaManager';
import ModalLoader from 'app/shared/components/ModalLoader';
import HeaderContents from "app/shared/components/HeaderContents";
import Constants from "app/shared/constants/constants";
import { RootState } from '../../../store';
import {
ForgotPasswordRequest, ForgotPasswordResponse, ForgotPasswordResponseType, Error,
} from "./types/ForgotPasswordType";

const ForgotPasswordForm = (): JSX.Element => {
    const { resetPasswordBrand } = getBrandName();
    const [isInvalid, setIsInvalid] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState(false);
    const [email, setEmail] = useState('');
    const [enableCTA, setEnableCTA] = useState(false);
    const {forgotPasswordMsg} = useSelector((state: RootState) => state.auth);
    const appInsights = useAppInsightsContext();
    const [forgotPassword] = useForgotPasswordMutation();
    const dispatch = useDispatch();

    if (appInsights) {
        appInsights.trackPageView({
            name: "ForgotPasswordForm",
        });
        const metricData = {
            average: 5,
            name: "React Component Engaged Time (seconds)",
            sampleCount: 1,
        };
        const additionalProperties = { "Component Name": 'ForgotPasswordForm' };
        appInsights.trackMetric(metricData, additionalProperties);
    }

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;

        setEmail(value);

        if (value && value !== "" && isEmailvalid(value)) {
            setEnableCTA(true);
            setIsInvalid(false);
        } else {
            setEnableCTA(false);
        }
    };

    const handleBlur = () => {
        if (email && email !== "" && isEmailvalid(email)) {
            setIsInvalid(false);
            setEnableCTA(true);
            return true;
        }
        setIsInvalid(true);
        setEnableCTA(false);
        return false;
    };

    const passwordReset = async () => {
        if (email) {
            setIsLoading(true);

            const recaptchaToken = await gRecaptchaManager.executeAndRetrieveToken(appInsights, "FORGOT_PASSWORD") as string;

            const creds: ForgotPasswordRequest = {
                pwReset: {
                    emailAddress: email,
                    brand: resetPasswordBrand,
                },
                recaptchaToken,
            };

            try {
                logging.info(appInsights, 'evt_ForgotPassword_req', "FORGOT_PASSWORD_SUBMIT", creds.pwReset.emailAddress);
                const response: ForgotPasswordResponseType = await forgotPassword(creds).unwrap();
                const tempValue: string = decrypt(response.value.toString());
                // eslint-disable-next-line
                const data: ForgotPasswordResponse = JSON.parse(tempValue);

                appInsights.trackEvent({
                    name: 'evt_ForgotPassword_resp',
                    properties: {
                        bodyValue: data,
                        user: creds.pwReset.emailAddress,
                        trace_id: localStorage.getItem('trace_id'),
                    },
                });
                logging.info(appInsights, 'evt_ForgotPassword_resp', "FORGOT_PASSWORD_SUCCESS", data);

                if (data?.header?.status === Constants.SUCCESS) {
                    dispatch(setForgotPasswordMsg(Constants.FORGOT_PASSWORD_SUCCESS));
                }
                setForgotPasswordSuccess(true);
                dispatch(setRedirectToMyAccount(false));
            } catch (e) {
                const {status} = (e as Error);
                if (status === 429) {
                    dispatch(setForgotPasswordMsg(Constants.FORGOT_PASSWORD_REPEAT));
                } else {
                    dispatch(setForgotPasswordMsg(Constants.FORGOT_PASSWORD_FAILURE));
                }
                setForgotPasswordSuccess(false);
                logging.error(appInsights, 'evt_ForgotPassword_err', "FORGOT_PASSWORD_FAILED", e);
            }
            setIsLoading(false);
            setEmail("");
            setEnableCTA(false);
        }
    };

    const handleResetPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        event.stopPropagation();
        passwordReset().then((res) => {
            logging.debug("_FORGOT_PASSWORD", "Forgot password response", res);
        }).catch((e) => {
            logging.debug("_FORGOT_PASSWORD_ERROR", "Forgot password error", e);
        });
    };

    return (
        <div className="form-container">
            <ModalLoader showModal={isLoading} />
            <HeaderContents title={Constants.FORGOT_PASSWORD_TITLE} subText={Constants.FORGOT_PASSWORD_SUBTEXT} />
            <Form noValidate className="mb-4">
                <Row>
                    <Col>
                    <div className="d-flex flex-column align-items-center mt-4">
                        <FormGroup className="d-flex flex-column mb-3 inputWidth" controlId="formBasicEmail">
                            <FormLabel className="signOn-subText">
                                Email Address
                                <span className="label-required">*</span>
                            </FormLabel>
                            <FormControl
                                className="checkout-input"
                                aria-required
                                isInvalid={isInvalid}
                                type="email"
                                name="emailAddress"
                                value={email}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                placeholder="Email Address"
                                aria-label="Email Address"
                                autoComplete="off"
                            />
                            <FormControl.Feedback type="invalid">
                                <BsExclamationTriangleFill size={16} className="errorImage" />
                                <span className="font-sub errorText">Please enter a valid email</span>
                            </FormControl.Feedback>
                        </FormGroup>
                        <Button
                            type="submit"
                            disabled={!enableCTA}
                            className="order-now-card-button mt-1 mb-1 inputWidth"
                            onClick={handleResetPassword}
                        >
                            Reset Password
                        </Button>
                    </div>
                    </Col>
                </Row>
                <p className="font-sub mt-4 text-center modalFooterNote">
                    NOTE: Email will come from “Customer Relations”
                    and the email address guest@tsg.pxsmail.com
                </p>
                <br/>
                <p className="font-sub text-center modalFooterNote mb-2">*Required Field</p>
                {forgotPasswordMsg
                && (
                    <Row className="d-flex align-items-center flex-column mx-1 mb-3">
                        <Col className="text-center w-auto">
                            <NotificationBanner message={forgotPasswordMsg} type={forgotPasswordSuccess ? "success" : "error"} onClose={() => dispatch(setForgotPasswordMsg(""))} />
                        </Col>
                    </Row>
                )}
            </Form>
        </div>
    );
};

export default ForgotPasswordForm;
